import type { AlignedHistory, HistoryRow } from '#shared/types';
import { cn } from '#client/lib/cn';
import { formatDate, dash } from '#client/lib/format';
import { sinceLastVisit } from './report-history.logic';
import { TrendArrow } from './TrendArrow';

type Visits = AlignedHistory['visits'];

/** This visit beside the earlier ones, one row per current parameter. */
export function HistoryTable({ visits, rows }: { visits: Visits; rows: readonly HistoryRow[] }) {
  return (
    <div className="c-table__scroll" tabIndex={0}>
      <table className="c-table c-history">
        <thead>
          <tr>
            <th scope="col" className="c-table__cell--frozen">
              Parameter
            </th>
            <th scope="col">This visit</th>
            {visits.map((visit) => (
              <th key={visit.report_id} scope="col">
                {formatDate(visit.visited_at)}
              </th>
            ))}
            <th scope="col">Reference</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <HistoryTableRow key={row.parameter_id} row={row} visits={visits} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryTableRow({ row, visits }: { row: HistoryRow; visits: Visits }) {
  const sinceLast = sinceLastVisit(row);

  return (
    <tr>
      <th scope="row" className="c-table__cell--frozen">
        {row.name}
      </th>
      <td>
        {/* The arrow belongs on **this** cell, not on the previous
            one: it describes how today's value moved. Placed on the
            June column it reads as a change that happened in June. */}
        <span className="c-history__cell">
          <span className={cn(row.current?.flag && `c-history__flag--${row.current.flag}`)}>
            {dash(row.current?.result)}
          </span>
          {sinceLast && <TrendArrow delta={sinceLast} />}
        </span>
      </td>
      {row.cells.map((cell, index) => (
        <td key={visits[index]?.report_id ?? index}>
          {cell ? (
            <span className="c-history__cell">
              <span className={cn(cell.flag && `c-history__flag--${cell.flag}`)}>
                {cell.result}
              </span>
              {cell.range_changed && (
                <abbr
                  className="c-history__marker"
                  title={`Reference range then: ${cell.normal_range}`}
                >
                  *
                </abbr>
              )}
            </span>
          ) : (
            <span className="c-muted">—</span>
          )}
        </td>
      ))}
      <td className="c-muted">{dash(row.normal_range)}</td>
    </tr>
  );
}
